<template>
  <div>
    <div class="note-card" v-if="note">
      <div class="note-card__header">
        <div class="note-header-content">
          <div class="note-card__title">NOTATKA</div>
          <div class="note-card__privacy">
            <EyeOffIcon />
            Widoczna tylko dla Ciebie
          </div>
        </div>
        <div class="note-header-right">
          <button
            class="note-action-button"
            @click="exportToPdf"
            :disabled="exporting"
          >
            {{ exporting ? "Eksportowanie..." : "Pobierz PDF" }}
          </button>
          <button
            v-if="allowDelete"
            class="note-action-button"
            @click="openDeleteModal"
          >
            Usuń
          </button>
        </div>
      </div>

      <div
        v-if="noteTitle"
        class="note-card__practice"
        :class="{ link: titleClickable }"
        @click="handleTitleClick"
      >
        {{ noteTitle }}
      </div>

      <div v-if="loading" class="note-card__info">Ładowanie notatki...</div>

      <div v-else>
        <div v-if="!isLogged" class="note-card__info">
          <div>Zaloguj się, aby zapisać notatkę z tej praktyki.</div>
          <button class="primary-button mt-4" @click="goToLogin">
            Zaloguj się
          </button>
        </div>

        <div v-else>
          <div class="note-card__preview">
            <template v-if="shouldTruncate && !isExpanded">
              {{ truncatedContent }}
            </template>
            <template v-else>
              {{ note.content }}
            </template>
          </div>
          <div v-if="shouldTruncate" class="note-card__expand">
            <span @click="toggleExpand" class="expand-link">
              {{ isExpanded ? "Zwiń" : "Rozwiń" }}
            </span>
          </div>

          <div
            v-if="!hasActiveSubscription"
            class="note-card__info text-center mt-2"
          >
            <div style="margin-bottom: 12px">
              Aby dodawać i edytować notatki, musisz posiadać aktywną
              subskrypcję.
            </div>
            <button class="primary-button" @click="goToSubscriptions">
              Wykup dostęp
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- <div v-else-if="!loading && isLogged && !note" class="note-card__info text-center">
      Nie masz jeszcze notatki do tej praktyki.
    </div> -->

    <v-dialog v-model="dialog" width="650">
      <v-card class="px-4 py-4" style="background-color: #d9d9d9">
        <h3 class="note-card__title" style="margin-bottom: 12px">
          {{ note ? "Edytuj notatkę" : "Dodaj notatkę" }}
        </h3>
        <v-textarea
          v-model="draftContent"
          label="Treść notatki"
          auto-grow
          outlined
          :disabled="saving"
          rows="4"
          ref="noteInput"
          :autofocus="dialog"
          :counter="maxChars"
          :rules="[characterLimitRule]"
          color="#54514C"
        />
        <!-- <div v-if="draftContent.length > maxChars" class="char-limit-warning">
          Przekroczono limit znaków ({{ draftContent.length }}/{{ maxChars }})
        </div> -->
        <div class="note-card__actions" style="margin-top: 12px">
          <button
            class="primary-button"
            :disabled="
              saving || !draftContent.trim() || draftContent.length > maxChars
            "
            @click="saveNote"
          >
            {{ saving ? "Zapisywanie..." : "Zapisz notatkę" }}
          </button>
          <button class="secondary-button" @click="closeDialog">Anuluj</button>
        </div>
      </v-card>
    </v-dialog>

    <BeforeDeleteModal
      :isVisible="deleteDialog"
      @closeModal="closeDeleteModal"
      @confirmDeletion="confirmDelete"
    />
  </div>
</template>

<script>
import BeforeDeleteModal from "./modals/BeforeDeleteModal.vue";
import EyeOffIcon from "./icons/EyeOffIcon.vue";
export default {
  name: "PracticeNote",
  props: {
    timePackageItemId: {
      type: String,
      required: true,
    },
    noteTitle: {
      type: String,
      default: "",
    },
    allowDelete: {
      type: Boolean,
      default: false,
    },
    showJournalLink: {
      type: Boolean,
      default: true,
    },
    titleClickable: {
      type: Boolean,
      default: false,
    },
    initialNote: {
      type: Object,
      default: null,
    },
    forBeginners: {
      type: Boolean,
      default: false,
    },
  },
  components: { BeforeDeleteModal, EyeOffIcon },
  data() {
    return {
      loading: false,
      draftContent: "",
      saving: false,
      dialog: false,
      deleteDialog: false,
      maxChars: 5000,
      isExpanded: false,
      truncateLimit: 300,
      exporting: false,
    };
  },
  computed: {
    note() {
      const storeNote = this.$store.getters["notes/getNoteByVideoId"](
        this.timePackageItemId
      );
      return storeNote || this.initialNote || null;
    },
    isLogged() {
      return !!this.$store.getters["auth/getToken"];
    },
    shouldTruncate() {
      return this.note && this.note.content.length > this.truncateLimit;
    },
    truncatedContent() {
      if (!this.note || !this.shouldTruncate) return "";
      return this.note.content.substring(0, this.truncateLimit) + "...";
    },
    hasActiveSubscription() {
      if (this.forBeginners) {
        return this.$store.getters["auth/getHasActiveSubscriptionForBeginners"];
      } else {
        return this.$store.getters["auth/getHasActiveSubscription"];
      }
    },
  },
  watch: {
    timePackageItemId: {
      immediate: true,
      handler(id) {
        if (id && this.isLogged) {
          this.fetchNote();
        }
      },
    },
    isLogged(logged) {
      if (logged && this.timePackageItemId) {
        this.fetchNote();
      }
    },
    dialog(value) {
      if (value) {
        this.$nextTick(() => {
          this.focusInput();
        });
      }
    },
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    characterLimitRule(value) {
      if (!value) return true;
      return (
        value.length <= this.maxChars ||
        `Maksymalna liczba znaków: ${this.maxChars}`
      );
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },
    goToLogin() {
      this.$router.push({
        name: "Auth",
        params: { previousPath: this.$route.fullPath },
      });
    },
    goToSubscriptions() {
      if (this.forBeginners) {
        this.$router.push({ name: "CoursesForBeginners" });
      } else {
        this.$router.push({ name: "Courses" });
      }
    },
    async fetchNote() {
      this.loading = true;
      await this.$store.dispatch(
        "notes/fetchNoteForVideo",
        this.timePackageItemId
      );
      this.loading = false;
    },
    openDialog() {
      if (!this.isLogged) {
        this.goToLogin();
        return;
      }
      this.draftContent = this.note ? this.note.content : "";
      this.dialog = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    closeDialog() {
      this.dialog = false;
      this.draftContent = "";
    },
    async saveNote() {
      if (!this.draftContent || !this.draftContent.trim()) {
        this.$store.dispatch(
          "snackbar/setSnackbar",
          {
            color: "red",
            icon: "exclamation-triangle",
            message: "Notatka nie może być pusta.",
          },
          { root: true }
        );
        this.$store.dispatch("snackbar/toggleSnackbar", true, { root: true });
        return;
      }
      this.saving = true;
      const savedNote = await this.$store.dispatch("notes/saveNote", {
        timePackageItemId: this.timePackageItemId,
        content: this.draftContent.trim(),
      });
      this.saving = false;
      if (savedNote) {
        this.closeDialog();
      }
    },
    openDeleteModal() {
      this.deleteDialog = true;
    },
    closeDeleteModal() {
      this.deleteDialog = false;
    },
    async confirmDelete() {
      if (!this.note) return;
      await this.$store.dispatch("notes/deleteNote", {
        noteId: this.note.id,
        timePackageItemId: this.timePackageItemId,
      });
      this.$emit("deleted");
      this.closeDeleteModal();
    },
    handleTitleClick() {
      if (this.titleClickable) {
        this.$emit("title-click");
      }
    },
    focusInput() {
      const ref = this.$refs.noteInput;
      if (ref && ref.$el) {
        const textarea = ref.$el.querySelector("textarea");
        if (textarea) {
          textarea.focus();
          return;
        }
      }
      if (ref && typeof ref.focus === "function") {
        ref.focus();
      }
      setTimeout(() => {
        if (ref && ref.$el) {
          const textarea = ref.$el.querySelector("textarea");
          if (textarea) {
            textarea.focus();
          }
        }
      }, 50);
    },
    async exportToPdf() {
      if (!this.note) return;

      this.exporting = true;
      await this.$store.dispatch("notes/exportNoteToPdf", this.note.id);
      this.exporting = false;
    },
  },
};
</script>

<style scoped>
.note-card {
  background: #f3f3f3;
  border-radius: 20px;
  padding: 20px;
  margin-top: 20px;
  box-shadow: none;
}
.note-card__header {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
  min-height: 60px;
}
.note-header-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}
.note-header-right {
  position: absolute;
  right: 0;
  top: 0;
  display: flex;
  gap: 12px;
  align-items: center;
}

@media (max-width: 768px) {
  .note-card__header {
    flex-direction: column-reverse;
    gap: 16px;
    min-height: auto;
  }
  .note-header-right {
    position: static;
    width: 100%;
    justify-content: center;
  }
}
.note-card__title {
  color: #8b857d;
  font-size: 34px;
  text-transform: uppercase;
  font-family: Vollkorn SC;
  font-weight: 400;
  text-align: center;
}
.note-card__privacy {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #54514c;
  font-weight: 500;
  margin-top: -15px;
}
.note-action-button {
  color: #6d6963;
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  text-decoration: underline;
  transition: all 0.3s;
  padding: 0;
}
.note-action-button:hover {
  color: #8b857d;
}
.note-action-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.note-card__practice {
  font-weight: 700;
  color: #54514c;
  margin-bottom: 8px;
  width: fit-content;
}
.note-card__practice.link {
  cursor: pointer;
}
.note-card__meta {
  font-size: 13px;
  color: #8b857d;
}
.note-card__info {
  color: #54514c;
  font-size: 14px;
}
.note-card__preview {
  white-space: pre-line;
  color: #54514c;
  font-size: 15px;
  margin-bottom: 8px;
  word-break: break-word;
  overflow-wrap: break-word;
}
.note-card__expand {
  margin-bottom: 8px;
}
.expand-link {
  color: #54514c;
  cursor: pointer;
  font-weight: 600;
  font-size: 16px;
}

.note-card__actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  align-items: center;
}
.note-card__editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.link-button {
  color: #8b857d;
  font-weight: 600;
}
.secondary-button {
  background: transparent;
  border: 1px solid #c5c0b9;
  color: #54514c;
  padding: 8px 16px;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s;
}
.secondary-button:hover {
  background: #f4f1ec;
}
.primary-button {
  cursor: pointer;
}
.delete-button {
  color: #373737;
  background: #d9d9d9;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  border-radius: 16px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 400;
  text-align: center;
  text-decoration: none;
  overflow-wrap: break-word;
  box-sizing: border-box;
  max-width: 300px;
  margin-top: 20px;
  padding: 13px 36px 13px 36px;
  font-family: "Inter";
  font-style: normal;
  text-transform: none !important;
  transition: all 0.3s;
}
.delete-button:hover {
  background: #e5e5e5;
}

.char-limit-warning {
  color: #c86a6a;
  font-size: 13px;
  margin-top: -8px;
  font-weight: 500;
}
</style>

<style>
/* Override Vuetify textarea colors to match new design */
.v-dialog .v-textarea.v-input--is-focused fieldset {
  border-color: #54514c !important;
}
.v-dialog .v-textarea .v-label--active {
  color: #54514c !important;
}
.v-dialog .v-textarea .v-counter {
  color: #8b857d !important;
}
</style>
